<template>
<div class="content">
    <div v-if="!course.id">
      <placeholder
        v-if="!error.serverError"
        title="Loading record"
      ></placeholder>
      <server-error v-bind:error="error.serverError"></server-error>
    </div>
    <div v-else>
      <!-- Navigation buttons -->
      <div class="btn-container u-pull-right u-text-right">
        <router-link
          :to="{ name: 'record-file', params: { id: course.candidate.id } }"
          class="action-button"
        >
          <button class="btn-dark action-button">
            <i
              class="fa-wrapper fa fa-folder-open normal-icon pad-right"
              aria-hidden="true"
            ></i>
            File
          </button>
        </router-link>
        <back-button></back-button>
      </div>
      <!-- Course detail -->
      <h2 class="font-light">{{ course.name }} Course</h2>
      <h4>{{ course.candidate.full_name }}</h4>
      <div>
        <strong>Course ID: </strong>
        <samp>{{ course.uuid }}</samp>
      </div>
      <div>
        <strong>Last Updated: </strong>
        {{ formatDateTime(course.last_updated) }}
      </div>
      <space></space>
      <server-error v-bind:error="error.serverError"></server-error>
      <space v-if="error.serverError"></space>
      <h4 class="font-light">Candidate</h4>
      <table class="table bordered">
        <tr>
          <th>Name</th>
          <td>{{ course.candidate.full_name }}</td>
          <th>ARN</th>
          <td>{{ course.candidate.arn }}</td>
        </tr>
      </table>

      <inactive-icon v-bind:is_active=course.is_active></inactive-icon>

      <space></space>
      <h4 class="font-light">Course Details</h4>
      <table class="table bordered">
        <tr>
          <th>Start Date</th>
          <th>Status</th>
          <th>End Date</th>
        </tr>
        <tr>
          <td>{{ formatDate(course.start_date) }}</td>
          <td>
            <div v-if="course.is_complete">
              <div
                class="tag tag--white tooltip tag--large"
                data-tooltip="Complete"
              >
                 <i
                  class="fa fa-wrapper fa-check-circle"
                  style="color: var(--cirrus-success);"
                >
                </i>
              </div>
              <span class="success">Completed</span>
              <div>
                <span class="font-bold">Certified By:</span>
                {{ course.completed_by.full_name }}
                ({{ course.completed_by.arn }})
              </div>
            </div>
            <!-- Dialog to allow HOO to complete course -->
            <div
              v-else-if="allItemsProficient &&
                         permissions.includes('courses.complete_course')"
              class="u-text-center"
            >
              <button
                class="u-inline outline btn-success"
                v-on:click="certifyCourseComplete"
                >
                <loading-spinner v-show="loading.complete"></loading-spinner>
                <i
                  v-show="!loading.complete"
                  class="fa fa-wrapper fa-check-circle pad-right"
                  style="color: var(--cirrus-white);"
                >
                </i>

                <strong>Certify Course Completed</strong>
              </button>
            </div>
            <div v-else>
              In progress.
            </div>
          </td>
          <td>{{ formatDate(course.end_date) }}</td>
        </tr>
      </table>

      <!-- Course Certificate links -->
      <div class="row" v-if="course.is_complete">
        <div class="col-12 u-text-right">
          <!-- Export record to PDF (email or direct download) -->
          <div class="u-inline u-fill-width u-text-right">
            <button class="btn-dark u-inline u-margin-right" v-on:click="email()">
              <loading-spinner v-show="loading.email"></loading-spinner>
              <span class="icon" v-show="!loading.email">
                <i class="fa-wrapper fa fa-envelope normal-icon pad-right"></i>
              </span>
              Email Certificate
            </button>
            <button class="btn-link u-inline" v-on:click="download()">
              <loading-spinner v-show="loading.download"></loading-spinner>
              <span class="icon" v-show="!loading.download">
                <i class="fa-wrapper fa fa-file-pdf normal-icon pad-right"></i>
              </span>
              Download Certificate
            </button>
          </div>
        </div>
      </div>
      <space></space>
      <button
        v-if="permissions.includes('records.add_record')"
        class="btn-info btn-small outline u-pull-right"
        v-on:click="addRecord"
      >
        <span class="icon">
          <i
            class="fa-wrapper fa fa-plus-circle"
            aria-hidden="true"
          ></i>
        </span>
        Add Record
      </button>
      <h4 class="font-light">Training Records</h4>
      <no-results-found
        v-if="course.records.length == 0"
        title="No Records Yet"
        message="The training records will appear here after they have been entered into the system.">
      </no-results-found>
      <div v-else>
        <table class="table bordered striped">
          <thead>
            <tr>
              <th class="u-text-center"></th>
              <th>Task</th>
              <th>Result</th>
              <th>Date</th>
              <th>Instructor</th>
              <th class="u-text-left u-hide-mobile" colspan="2">
                <!-- Aircraft, status, etc. -->
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in course.records" v-bind:key="record.id">
              <td>
                <record-action-button v-bind:record="record">
                </record-action-button>
              </td>
              <td class="u-text-left" style="white-space: nowrap;">
                <record-repeat-icon v-bind:isRepeat="record.is_repeat">
                </record-repeat-icon>
                {{ record.task_code }}:
                {{ record.task_name }}
              </td>
              <td>
                <div class="tag-container u-left">
                  <status-tag
                    v-if="record.is_closed"
                    v-bind:status="record.status"
                    v-bind:label="record.result"
                  ></status-tag>
                </div>
              </td>
              <td>{{ formatDate(record.date) }}</td>
              <td>{{ record.instructor }}</td>
              <td class="u-hide-mobile">
                {{ record.aircraft_identifier }}
                <span v-if="record.aircraft_type">
                  ({{ record.aircraft_type }})
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <space></space>
      <h4 class="font-light">Hours Summary</h4>
      <no-results-found
        v-if="course.hours.length == 0"
        title="No Flight Hours Yet"
        message="The flight hours will appear here after training records are added which include flight hours.">
      </no-results-found>
      <table v-else class="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Task</th>
            <th>Aircraft</th>
            <th
              v-for="(entry ,idx) in Object.entries(course.hours[0]).slice(5)"
              v-bind:key="idx"
            >
              {{ entry[0] }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in course.hours"
            v-bind:key="idx"
          >
            <td>{{ formatShortDate(row.date) }}</td>
            <td>{{ row.task }}</td>
            <td>
              <div>{{ row.identifier }}</div>
              <div>{{ row.type }}</div>
            </td>
            <td
              v-for="(entry, subidx) in Object.entries(row).slice(5)"
              v-bind:key="idx-subidx"
            >
              {{ entry[1] }}
            </td>
          </tr>
          <tr>
            <th colspan="3" style="text-align: right !important">
              TOTAL
            </th>
            <th
              v-for="(key, idx) in  Object.keys(course.hours[0]).slice(5)"
              v-bind:key="`hours-totals-${idx}`"
              >
              {{ getHourTotals(key) }}
            </th>
          </tr>
        </tbody>
      </table>

      <space></space>
      <h4 class="font-light">Achievement Record</h4>
      <table class="table bordered striped">
        <thead>
          <tr>
            <th colspan="2">Competency</th>
            <th colspan="2"><span v-if="showStatus2">First</span> Assessment</th>
            <th colspan="2" v-if="showStatus2">Second Assessment</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in course.achievements"
            v-bind:key="item.id"
          >
            <td>
              {{ item.competency_reference }}
            </td>
            <td class="u-text-left">
              {{ item.competency_name }}
            </td>
            <td>
              <item-status v-bind:item="getMarkerStatus(item, 1)"></item-status>
            </td>
            <td>
              <div v-if="item.assessor_1">
                {{ item.assessor_1.first_name[0] }}.{{ item.assessor_1.last_name }}
                ({{ item.assessor_1.arn }})
              </div>
              <div>{{ formatDate(item.date_1) }}</div>
            </td>
            <td v-if="showStatus2">
              <item-status v-bind:item="getMarkerStatus(item, 2)"></item-status>                        </td>
            <td v-if="showStatus2">
              <div v-if="item.assessor_2">
                {{ item.assessor_2.first_name[0] }}.{{ item.assessor_2.last_name }}
                ({{ item.assessor_1.arn }})
              </div>
              <div>{{ formatDate(item.date_2) }}</div>
            </td>
          </tr>
        </tbody>
      </table>


    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import ServerError from "@/components/ServerError";
import Placeholder from "@/components/Placeholder";
import NoResultsFound from "@/components/PlaceholderNoResults";
import InactiveIcon from "@/components/InactiveIcon";
import ItemStatus from "@/components/records/ItemStatus";
import StatusTag from "@/components/StatusTag";
import RecordRepeatIcon from "@/components/records/RecordRepeatIcon";
import RecordActionButton from "@/components/records/RecordActionButton";
import BackButton from "@/components/BackButton";
import LoadingSpinner from "@/components/LoadingSpinner";
import { sumProperties } from "@/functions/sumProperties.function";
import { scrollToTop } from "@/functions/scrollToTop";
import { DateFormatter, ShortDateFormatter } from "@/functions/dateFormat.function";
import { DateTimeFormatter } from "@/functions/dateTimeFormat.function";

// File download components
import CourseService from "@/services/courses.service";
import { handleFileDownload } from "@/functions/handleFileDownload.function";
import { handleError } from "@/functions/handleError.function";

export default {
  name: "course-detail",
  components: {
    InactiveIcon,
    ItemStatus,
    StatusTag,
    RecordRepeatIcon,
    RecordActionButton,
    BackButton,
    ServerError,
    Placeholder,
    NoResultsFound,
    LoadingSpinner
  },
  computed: {
    ...mapGetters("core", ["error",]),
    ...mapGetters("auth", ["permissions",]),
    ...mapGetters("courses", ["course", "loading"]),
    ...mapGetters("records", ["markers", "reasons"]),
    allItemsProficient: function() {
      if (this.course) {
        var not_proficient = this.course.achievements.find(
          a => a["status_" + this.course.number_proficient] != 0);
        return !not_proficient
      }
      return false
    },
    showStatus2: function() {
      return this.course.number_proficient == 2;
    }
  },
  methods: {
    formatDate: function(val) {
      return DateFormatter(val);
    },
    formatShortDate: function(val) {
      return ShortDateFormatter(val);
    },
    formatDateTime: function(val) {
      return DateTimeFormatter(val);
    },
    getMarkerStatus(item, idx) {
      if (!this.markers) { return undefined };
      let marker = this.markers.find(m => m.code == item[`marker_code_${idx}`]);
      return {
        status: marker.status,
        requires_reason: marker.requires_reason,
        marker_code: marker.code,
        marker_name: marker.name
      }
    },
    download: function() {
      this.$store.commit("courses/loading", { download: true });
      CourseService.getCourseCertificate(this.course.id)
        .then(response => {
          this.$store.commit("courses/loading", { download: false });
          let link = handleFileDownload(response);
          link.click();
          setTimeout(() => {
            link.remove();
            window.URL.revokeObjectURL(link.href);
          }, 5000);
        })
        .catch(error => {
          this.$store.commit("courses/loading", { download: false });
          var data = handleError(error);
          this.$store.commit("core/setError", data);
        });
    },
    email: function() {
          this.$store.commit("courses/loading", { email: true });
      CourseService.getCourseCertificateEmail(this.course.id)
        .then(() => {
          this.$store.commit("courses/loading", { email: false });
        })
        .catch(error => {
          this.$store.commit("courses/loading", { email: false });
          var data = handleError(error);
          this.$store.commit("core/setError", data);
        });
    },
    getHourTotals: function(field) {
      return sumProperties(this.course.hours, field);
    },
    certifyCourseComplete: function() {
      let data = { id: this.$route.params.id };
      this.$store.dispatch("courses/completeCourse", data);
    },
    addRecord: function() {
      this.$router.push({
        name: "record-create",
        query: { candidate: this.course.candidate.id }
      });
    }
  },
  mounted() {
    this.$store.commit("core/clearErrors");
    let data = { id: this.$route.params.id }
    this.$store.dispatch("courses/getCourse", data);
    this.$store.dispatch("records/getMarkers");
    scrollToTop();
  }
}
</script>
